import {catchError, filter, first, map} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {PermissionService} from './permission.service';

interface FeatureToggle {
  feature: string;
  visible_on_envs: string[];
}

@Injectable({
  providedIn: 'root',
})
export class FeatureTogglesService {
  private featureConfig$ = new BehaviorSubject<FeatureToggle[] | null>(null);
  private currentEnv: string;

  constructor(
    private http: HttpClient,
    private permissionService: PermissionService,
  ) {
    this.currentEnv = this.determineEnvironment();
    this.loadFeatureConfig();
  }

  private determineEnvironment(): string {
    const url = window.location.href;

    if (url.startsWith('http://localhost') || url.startsWith('https://localhost')) {
      return 'local';
    } else if (url.startsWith('https://dev-viba.mobileinsight.com/')) {
      return 'dev';
    } else if (url.startsWith('https://dev2-viba.mobileinsight.com/')) {
      return 'dev2';
    } else if (url.startsWith('https://qa-viba.mobileinsight.com/')) {
      return 'qa';
    }  else if (url.startsWith('https://staging-viba.mobileinsight.com/')) {
      return 'staging';
    } else if (url.startsWith('https://viba.mobileinsight.com/')) {
      return 'prod';
    }

    return 'unknown'; // Default or fallback
  }

  private loadFeatureConfig(): void {
    const cacheBustingUrl = `/assets/feature-config.json?v=${new Date().getTime()}`;
    this.http
      .get<FeatureToggle[]>(cacheBustingUrl)
      .pipe(
        catchError((error) => {
          console.error('Failed to load feature configuration', error);
          return of([]);
        })
      )
      .subscribe((config) => {
        this.featureConfig$.next(config);
      });
  }

  isEnabled(featureName: string): Observable<boolean> {
    return this.featureConfig$.pipe(
      filter((config) => config !== null), // Wait until config is loaded
      first(),
      // Check if feature is enabled
      map((config) => {
        const feature = config!.find((f) => f.feature === featureName);
        if (!feature) {
          console.warn(`Feature "${featureName}" not found in configuration.`);
          return false;
        }
        return feature.visible_on_envs.includes(this.currentEnv);
      })
    );
  }

  public isPermissionEnabled(permissionName: string, featureFlag: string): Observable<boolean> {
    return combineLatest([
      this.isEnabled(featureFlag),
      this.permissionService.hasPermission(permissionName)
    ]).pipe(
      map(([isEnabled, hasPermission]) => isEnabled && hasPermission)
    );
  }
}
